import { useEffect, useState } from 'react';
import { Drawer } from '../ui/Drawer';
import { StatusPill } from '../ui/StatusPill';
import { AdmissionStatus } from './AdmissionStatus';
import {
  classificationSourceLabel,
  formatLegalRegimes,
  instrumentTypeLabel,
  ruleOutcomeLabel,
  sourceFormLabel,
  vaultRoleLabel,
} from '../../admission/labels';
import { requiredFactCodes } from '../../admission/selectors';
import { getDocumentAdmission } from '../../api/originatorApi';
import { AdmissionFactsForm } from './AdmissionFactsForm';

function ReadOnlyFact({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-eorig-border py-2 last:border-b-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">{label}</dt>
      <dd className="min-w-0 text-right text-[13px] text-eorig-ink">{children ?? '—'}</dd>
    </div>
  );
}

function relationshipLabel(item) {
  const type = item.relationship_type || 'RELATED';
  const target = item.to_document_id || 'unspecified';
  return `${type} → ${target}`;
}

export function AdmissionDetailsDrawer({
  open,
  onClose,
  documentId,
  packageId,
  packageAdmission,
  fileName,
  factsReadOnly = false,
  onAdmissionUpdated,
  onFactsActivityChange,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !documentId) {
      setDetail(null);
      setError('');
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getDocumentAdmission(documentId);
        if (!cancelled) setDetail(response);
      } catch (requestError) {
        if (!cancelled) {
          setDetail(null);
          setError(requestError.message || 'Unable to load admission details.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, documentId, packageAdmission?.row_version, packageAdmission?.assessment_hash]);

  const summary = (packageAdmission?.document_assessments || []).find(
    (row) => String(row.document_id) === String(documentId)
  );
  const view = detail || summary;
  const factExceptions = requiredFactCodes(packageAdmission, documentId);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Admission details"
      description={fileName || (documentId ? `Document ${documentId}` : 'Select a document')}
    >
      {!documentId ? (
        <p className="text-sm text-eorig-muted">Select a document to review admission details.</p>
      ) : loading && !view ? (
        <p className="text-sm text-eorig-muted">Loading admission details…</p>
      ) : error && !view ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : (
        <div className="space-y-5">
          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
              System-derived classification
            </h3>
            <p className="mt-1 text-[12px] text-eorig-muted">
              Read-only. The Originator cannot turn this classification into an
              authoritative decision.
            </p>
            <dl className="mt-2 rounded-lg border border-eorig-border bg-eorig-surface/50 px-3">
              <ReadOnlyFact label="Instrument type">
                {instrumentTypeLabel(view?.instrument_type)}
              </ReadOnlyFact>
              <ReadOnlyFact label="Legal regimes">
                {formatLegalRegimes(view?.legal_regimes)}
              </ReadOnlyFact>
              <ReadOnlyFact label="eVault treatment">{vaultRoleLabel(view?.vault_role)}</ReadOnlyFact>
              <ReadOnlyFact label="Document role">{vaultRoleLabel(view?.vault_role)}</ReadOnlyFact>
              <ReadOnlyFact label="Source form">{sourceFormLabel(view?.source_form)}</ReadOnlyFact>
              <ReadOnlyFact label="Classification source">
                {classificationSourceLabel(view?.classification_source)}
              </ReadOnlyFact>
              <ReadOnlyFact label="Confidence">
                {view?.confidence === undefined || view?.confidence === null
                  ? '—'
                  : view.confidence}
              </ReadOnlyFact>
              <ReadOnlyFact label="Admission check">
                {view?.admission_check ? (
                  <AdmissionStatus kind="check" status={view.admission_check} />
                ) : (
                  '—'
                )}
              </ReadOnlyFact>
            </dl>
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
              Evidence references
            </h3>
            {(view?.evidence_references || []).length ? (
              <ul className="mt-2 space-y-1 text-[13px] text-eorig-ink">
                {(view.evidence_references || []).map((code) => (
                  <li key={code}>{code}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[13px] text-eorig-muted">None recorded.</p>
            )}
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
              Relationships
            </h3>
            {(detail?.relationships || packageAdmission?.relationships || []).filter(
              (item) =>
                !documentId ||
                String(item.from_document_id) === String(documentId) ||
                String(item.to_document_id) === String(documentId)
            ).length ? (
              <ul className="mt-2 space-y-1 text-[13px] text-eorig-ink">
                {(detail?.relationships || packageAdmission?.relationships || [])
                  .filter(
                    (item) =>
                      !documentId ||
                      String(item.from_document_id) === String(documentId) ||
                      String(item.to_document_id) === String(documentId)
                  )
                  .map((item, index) => (
                    <li key={item.id || index}>{relationshipLabel(item)}</li>
                  ))}
              </ul>
            ) : (
              <p className="mt-2 text-[13px] text-eorig-muted">No relationships recorded.</p>
            )}
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
              Rule results
            </h3>
            {(detail?.rule_results || []).length ? (
              <ul className="mt-2 space-y-2">
                {detail.rule_results.map((rule) => (
                  <li
                    key={rule.id || `${rule.rule_code}-${rule.evaluated_at}`}
                    className="rounded-lg border border-eorig-border px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[12px] font-semibold text-eorig-ink">
                        {rule.rule_code}
                      </span>
                      <StatusPill
                        value={rule.result}
                        label={ruleOutcomeLabel(rule.result)}
                      />
                    </div>
                    {rule.message ? (
                      <p className="mt-1 text-[12px] text-eorig-muted">{rule.message}</p>
                    ) : null}
                    {rule.exception_code ? (
                      <p className="mt-1 font-mono text-[11px] text-eorig-ink">
                        {rule.exception_code}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[13px] text-eorig-muted">No rule results for this document.</p>
            )}
          </section>

          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
              Blockers and warnings
            </h3>
            {[...(detail?.blockers || []), ...(detail?.warnings || [])].length ? (
              <ul className="mt-2 space-y-2">
                {[...(detail?.blockers || []), ...(detail?.warnings || [])].map((item, index) => (
                  <li key={item.id || `${item.code}-${index}`} className="text-[13px] text-eorig-ink">
                    <span className="font-mono font-semibold">{item.code}</span>
                    {item.description ? ` — ${item.description}` : ''}
                    {item.fact_code ? (
                      <span className="block text-[12px] text-eorig-muted">
                        Linked fact: {item.fact_code}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-[13px] text-eorig-muted">No blockers or warnings for this document.</p>
            )}
          </section>

          {factExceptions.length ? (
            <section>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-eorig-muted">
                Required confirmations
              </h3>
              <ul className="mt-2 space-y-1 text-[13px] text-eorig-ink">
                {factExceptions.map((item) => (
                  <li key={item.id || item.fact_code}>
                    Required confirmation: {item.fact_code}
                    {item.code ? ` (${item.code})` : ''}
                    {item.description ? ` — ${item.description}` : ''}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
      <div className="mt-5">
        <AdmissionFactsForm
          packageId={packageId}
          documentId={documentId}
          admission={packageAdmission}
          readOnly={factsReadOnly}
          onAdmissionUpdated={onAdmissionUpdated}
          onActivityChange={onFactsActivityChange}
        />
      </div>
    </Drawer>
  );
}
